import { useMemo } from 'react';
import { findLatestPlanningScan } from './scanTakenAt';
import { useDmScans, type DmScansQueryConfig } from './useDmScansQueries';
import { scanDecision, type ScanDecision } from './scanInstructionTone';

export interface MonitoringTabBadgeProps {
    queries: DmScansQueryConfig;
    patientId: string | null;
    dmId: number | null | undefined;
}

interface InstructionTone {
    className: string;
    label: string;
    title: string;
}

// Same traffic-light reading as InstructionPill and the selector circles; keep
// the three in step or a badge and its scan disagree.
const INSTRUCTION_TONES: Record<ScanDecision, InstructionTone> = {
    go: {
        className: 'bg-emerald-50 text-emerald-700',
        label: 'Go',
        title: 'Latest scan: the patient can move to the next aligner'
    },
    nogo: {
        className: 'bg-amber-50 text-amber-700',
        label: 'No-Go',
        title: 'Latest scan: the patient should stay on the current aligner'
    },
    go_back: {
        className: 'bg-red-50 text-red-700',
        label: 'Go-Back',
        title: 'Latest scan: the patient should go back to a previous aligner'
    },
    start: {
        className: 'bg-gray-100 text-gray-600',
        label: 'Start',
        title: 'Latest scan: first scan of the series. Not a Go/No-Go decision.'
    }
};

const NO_INSTRUCTION_TONE: InstructionTone = {
    className: 'bg-gray-100 text-gray-600',
    label: 'Pending',
    title: 'Latest scan has no instruction yet'
};

const BADGE_CLASS =
    'inline-flex items-center justify-center min-w-[1.25rem] px-1.5 py-0.5 rounded-full text-[11px] font-semibold leading-4 tabular-nums';

/** Aligner number + DM decision for the newest scan, sized for a tab label. */
function MonitoringTabBadge({ queries, patientId, dmId }: MonitoringTabBadgeProps) {
    // Shares the tab's query key, so this reads the cache rather than refetching.
    const { data: list } = useDmScans(queries, patientId, dmId);
    const scans = list?.scans;
    const tracksAligners = list?.tracksAligners ?? true;

    const latestScan = useMemo(() => findLatestPlanningScan(scans ?? []), [scans]);

    if (!scans || scans.length === 0) return null;

    // No aligners means no decision to report, ever — so the badge counts the scans
    // instead. "Pending" would promise an instruction that is never coming.
    if (!tracksAligners) {
        const count = scans?.length ?? 0;
        const label = `${count} scan${count === 1 ? '' : 's'}`;
        return (
            <span
                title={`${label} · this monitoring issues no aligner instructions`}
                aria-label={label}
                className={`${BADGE_CLASS} bg-gray-100 text-gray-600`}
            >
                {count}
            </span>
        );
    }

    if (!latestScan) return null;
    const decision = scanDecision(latestScan);
    const tone = decision ? INSTRUCTION_TONES[decision] : NO_INSTRUCTION_TONE;
    const aligner = latestScan.alignerIndex;

    return (
        <span
            title={tone.title}
            aria-label={`Aligner ${aligner ?? 'unknown'} · ${tone.label}`}
            className={`${BADGE_CLASS} ${tone.className}`}
        >
            {aligner ?? '?'}
        </span>
    );
}

export { MonitoringTabBadge };
